<template>
  <v-dialog
    v-model="showDialog"
    persistent
    scrollable
    width="90%"
    max-width="1200px"
  >
    <v-card class="pa-4">
      <v-card-title class="d-flex justify-space-between">
        <span
          class="text-lg-subtitle-1 text-xl-h6 text-uppercase font-weight-black primary--text"
        >
          bulk upload contract rates
        </span>
        <v-btn depressed fab class="mt-n3" small @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row class="pt-8">
          <v-col cols="5" class="d-flex">
            <v-file-input
              v-model="file"
              hide-details
              dense
              label="upload document"
              prepend-inner-icon="mdi-attachment mdi-rotate-90"
              class="mr-4"
              prepend-icon=""
              outlined
              accept=".xlsx, .xls"
              @change="getFileData"
            />
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-icon color="primary" v-on="on" v-bind="attrs">
                  mdi-information
                </v-icon>
              </template>
              <span>
                Columns required: Route, Job Type, Container Type, Container
                Count, Rate
              </span>
            </v-tooltip>
          </v-col>
          <v-col cols="7" class="d-flex justify-end text-capitalize">
            <v-btn class="primary" depressed @click="downloadSampleExcel">
              download sample excel file
            </v-btn>
          </v-col>
          <v-col cols="12" class="d-flex justify-space-between pt-0">
            <span class="text-capitalize">
              total records : {{ totalRecordCount }}
            </span>
            <span>records with error : {{ rowData.length }}</span>
          </v-col>
          <v-col v-if="distinctErrors.length > 0" class="py-0">
            <v-alert border="left" colored-border color="red">
              <v-row no-gutters>
                <v-col class="grow"></v-col>
                <v-col class="shrink">
                  <v-btn
                    class="error"
                    depressed
                    @click="removeAllRecordWithError"
                  >
                    remove all with error
                  </v-btn>
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
          <v-col cols="12" v-if="rowData.length > 0">
            <AgGridVue
              @grid-ready="gridReady"
              :grid-options="gridOptions"
              :column-defs="columnDefs"
              :default-col-def="defaultColDef"
              :context="context"
              :row-data="rowData"
              style="width: 100%; height: 400px"
              class="ag-theme-material"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="pa-4 d-flex justify-end">
        <v-btn
          color="primary"
          :loading="loading"
          :disabled="!file || rowData.length != 0"
          @click="submitData"
        >
          submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { read, utils } from "xlsx";
import { AgGridVue } from "ag-grid-vue";
import RemoveRowButtonBulkUpload from "@/components/General/RemoveRowButtonBulkUpload.vue";
import BulkUploadErrorTooltip from "@/components/General/BulkUploadErrorTooltip.vue";
import { downloadBlobData } from "@/utils/functions.js";

export default {
  components: {
    AgGridVue,
    RemoveRowButtonBulkUpload,
    BulkUploadErrorTooltip,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    contractId: {
      type: Number,
    },
  },
  data() {
    return {
      loading: false,
      file: null,
      rowData: [],
      columnDefs: [],
      gridApi: null,
      columnApi: null,
      gridOptions: {
        headerHeight: 40,
        rowHeight: 40,
        rowSelection: "multiple",
        suppressRowClickSelection: true,
        suppressDragLeaveHidesColumns: true,
        enableCellTextSelection: true,
      },
      defaultColDef: {
        lockPosition: true,
      },
      serverErrors: [],
      dataObject: [],
    };
  },
  computed: {
    context() {
      return { parentComponent: this };
    },
    totalRecordCount() {
      return this.dataObject.filter((item) => Object.keys(item).length > 0)
        .length;
    },
    showDialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    distinctErrors() {
      let err = [];
      this.serverErrors
        .filter((e) => Object.keys(e).length > 0)
        .map((m) => Object.keys(m).map((k) => m[k]))
        .forEach((r) => {
          r.forEach((s) => {
            err = [...new Set([...err, ...s])];
          });
        });
      return err;
    },
  },
  watch: {
    value(val) {
      if (!val) {
        this.clearDialogData();
      }
    },
  },
  methods: {
    gridReady(params) {
      this.gridApi = params.api;
      this.columnApi = params.columnApi;
    },
    getFileData(file) {
      if (!file) {
        this.clearDialogData();
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const wb = read(reader.result, {
          type: "binary",
          cellText: false,
          cellDates: true,
        });
        const rowData = utils.sheet_to_row_object_array(
          wb.Sheets[wb.SheetNames[0]],
          {
            header: 0,
            defval: "",
            raw: false,
          }
        );
        this.dataObject = rowData.map((row, index) => {
          const newObject = { oldIndex: index };
          for (const [key, value] of Object.entries(row)) {
            const newKey = key.toString().toLowerCase().trim().replaceAll(" ", "_");
            newObject[newKey] = value;
          }
          return newObject;
        });
      };
      reader.readAsBinaryString(file);
    },
    removeErrorRow(index, oldIndex) {
      setTimeout(() => {
        this.serverErrors.splice(index, 1);
        this.rowData.splice(index, 1);
        this.dataObject.splice(
          this.dataObject.findIndex((item) => item.oldIndex == oldIndex),
          1
        );
      }, 200);
    },
    removeAllRecordWithError() {
      while (this.rowData && this.rowData.length > 0) {
        this.removeDataFromRow(this.rowData[0].oldIndex, 0);
      }
    },
    removeDataFromRow(oldIndex, currentIndex) {
      this.dataObject.splice(oldIndex, 1, {});
      this.rowData.splice(currentIndex, 1);
      const data = this.dataObject.filter((obj) => Object.keys(obj).length !== 0);
      if (data.length == 0) {
        this.clearDialogData();
      }
      if (this.rowData.length == 0) {
        this.serverErrors = [];
      }
    },
    cellStyle(param) {
      if (
        param &&
        param.colDef &&
        param.colDef.field &&
        this.serverErrors &&
        this.serverErrors[param.rowIndex] &&
        this.serverErrors[param.rowIndex][param.colDef.field]
      ) {
        return { backgroundColor: "red" };
      }
      return { backgroundColor: "white" };
    },
    dataChange(param) {
      const field = param.colDef.field;
      const localError = this.serverErrors[param.node.rowIndex];
      if (param && param.newValue != param.oldValue && localError) {
        delete localError[field];
      }
      const newIndex = this.dataObject.findIndex(
        (item) => item.oldIndex == param.data.oldIndex
      );
      this.dataObject[newIndex] = param.data;

      if (
        localError &&
        Object.keys(localError).filter((key) => key != "non_field_errors").length == 0
      ) {
        setTimeout(() => {
          this.serverErrors.splice(param.node.rowIndex, 1);
          this.rowData.splice(param.node.rowIndex, 1);
        }, 200);
      }
      return param.newValue;
    },
    downloadSampleExcel() {
      const params = {
        excel_type: "bulk_customer_contract_v2_rate",
      };
      this.$api.bulkUpload
        .downloadSampleFile(params)
        .then((res) => {
          downloadBlobData(res, "Sample bulk_customer_contract_v2_rate excel file.xlsx");
          this.$bus.$emit("showToastMessage", {
            message: "Download file successfully",
            color: "success",
          });
        })
        .catch(() => {
          this.$bus.$emit("showToastMessage", {
            message: "Can't download file!",
            color: "error",
          });
        });
    },
    submitData() {
      this.serverErrors = [];
      const rows = this.dataObject.filter(
        (item) => Object.keys(item).length > 0
      );
      if (!rows.length) {
        this.$bus.$emit("showToastMessage", {
          message: "Data not found!",
          color: "error",
        });
        return;
      }
      this.loading = true;
      this.$bus.$emit("showLoader", true);
      this.$api.customerContractV2
        .bulkUploadCustomerContractV2Rates({ id: this.contractId, data: rows })
        .then((res) => {
          this.$bus.$emit("showLoader", false);
          this.loading = false;
          this.$bus.$emit("showToastMessage", {
            message: (res.data && res.data.message) || "Successfully uploaded file",
            color: "success",
          });
          this.$emit("submitted");
          this.closeDialog();
        })
        .catch((err) => {
          this.$bus.$emit("showLoader", false);
          this.loading = false;
          if (Array.isArray(err.data)) {
            this.serverErrors = err.data;
            this.generateErrorRow(err.data);
            this.$bus.$emit("showToastMessage", {
              message: "Error uploading sheet!",
              color: "error",
            });
          } else {
            this.$bus.$emit("showToastMessage", {
              message:
                (err.data && err.data.non_field_errors && err.data.non_field_errors.join()) ||
                "Couldn't upload Rates!",
              color: "error",
            });
          }
        });
    },
    generateErrorRow(errorRow) {
      const errorIndexRow = errorRow.map((item) => Object.keys(item).length > 0);
      this.columnDefs = Object.keys(this.dataObject[0]).map((item) => ({
        headerName: `${item.replaceAll("_", " ")}`.toLowerCase().toUpperCase(),
        field: item,
        editable: "oldIndex" != item,
        hide: "oldIndex" == item || "formError" == item,
        valueParser: this.dataChange,
        cellStyle: this.cellStyle,
      }));

      this.columnDefs.unshift({
        pinned: "left",
        headerName: "errors",
        width: 90,
        cellRendererFramework: "BulkUploadErrorTooltip",
      });

      this.columnDefs.push({
        headerName: "actions",
        pinned: "right",
        width: 90,
        cellRendererFramework: "RemoveRowButtonBulkUpload",
      });

      this.serverErrors = this.serverErrors.filter((item, index) => errorIndexRow[index]);
      this.rowData = this.dataObject.filter((item, index) => errorIndexRow[index]);
      this.rowData = this.rowData.map((row, index) => {
        const error = this.serverErrors[index];
        return { ...row, formError: error };
      });
    },
    closeDialog() {
      this.showDialog = false;
      this.clearDialogData();
    },
    clearDialogData() {
      this.file = null;
      this.rowData = [];
      this.columnDefs = [];
      this.dataObject = [];
      this.serverErrors = [];
    },
  },
};
</script>
